import { motion } from "framer-motion";
import { Users, Snowflake, Fuel, Gauge, ArrowUpRight } from "lucide-react";
import { whatsappLink } from "@/lib/whatsapp";

const SEDAN_DZIRE = "https://upload.wikimedia.org/wikipedia/commons/thumb/e/e7/Maruti_Suzuki_Swift_Dzire_%28cropped%29.jpg/1280px-Maruti_Suzuki_Swift_Dzire_%28cropped%29.jpg";
const SEDAN_ETIOS = "https://upload.wikimedia.org/wikipedia/commons/thumb/9/9c/Toyota_Etios_%285989331111%29.jpg/1280px-Toyota_Etios_%285989331111%29.jpg";
const MPV_ERTIGA = "https://upload.wikimedia.org/wikipedia/commons/thumb/f/f9/Suzuki_Ertiga_NC_FL_1.5_GLX_Hybrid_Snow_White_Pearl.jpg/1280px-Suzuki_Ertiga_NC_FL_1.5_GLX_Hybrid_Snow_White_Pearl.jpg";
const SUV_INNOVA = "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d3/Toyota_Kijang_Innova_%28First_Generation%29.jpg/1280px-Toyota_Kijang_Innova_%28First_Generation%29.jpg";
const SUV_CRYSTA = "https://upload.wikimedia.org/wikipedia/commons/thumb/8/87/Toyota_Innova_Crysta_2.4_Z_front_right.jpg/1280px-Toyota_Innova_Crysta_2.4_Z_front_right.jpg";
const VAN_TEMPO = "https://upload.wikimedia.org/wikipedia/commons/thumb/4/43/Force_Traveller_Luxury.jpg/1280px-Force_Traveller_Luxury.jpg";
const LUXURY = "https://upload.wikimedia.org/wikipedia/commons/thumb/5/55/Mercedes-Benz_W223_IMG_6663.jpg/1280px-Mercedes-Benz_W223_IMG_6663.jpg";

const FLEET = [
  { name: "Swift Dzire", image: SEDAN_DZIRE, seats: 4, ac: true, tag: "Compact Sedan" },
  { name: "Toyota Etios", image: SEDAN_ETIOS, seats: 4, ac: true, tag: "Sedan" },
  { name: "Maruti Ertiga", image: MPV_ERTIGA, seats: 6, ac: true, tag: "MPV" },
  { name: "Toyota Innova", image: SUV_INNOVA, seats: 7, ac: true, tag: "SUV" },
  { name: "Innova Crysta", image: SUV_CRYSTA, seats: 7, ac: true, tag: "Premium SUV" },
  { name: "Tempo Traveller", image: VAN_TEMPO, seats: 12, ac: true, tag: "Group Van" },
  { name: "Luxury Car", image: LUXURY, seats: 4, ac: true, tag: "Chauffeured" },
];

export default function Fleet() {
  return (
    <section id="fleet" className="relative bg-brand-ink text-white py-24 md:py-32 overflow-hidden" data-testid="fleet-section">
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-brand-blue/30 blur-3xl" />
      <div className="absolute -bottom-40 -left-32 w-[500px] h-[500px] rounded-full bg-brand-orange/20 blur-3xl" />

      <div className="relative max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-orange">Chapter III · The Fleet</div>
            <h2 className="mt-4 font-display font-black text-5xl md:text-6xl leading-[1.02]">
              Seven silhouettes. <br/>
              <span className="italic text-brand-orange font-medium">One promise.</span>
            </h2>
          </div>
          <p className="max-w-md text-white/70">From nimble sedans to twelve-seat tempos — spotless, sanitized, and chauffeured with quiet confidence.</p>
        </div>

        {/* Horizontal scroll snap fleet */}
        <div className="mt-14 -mx-4 md:-mx-8 px-4 md:px-8 flex gap-5 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-6">
          {FLEET.map((v, i) => (
            <motion.div
              key={v.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: i * 0.05 }}
              className="snap-start shrink-0 w-[320px] md:w-[380px] rounded-[28px] bg-white text-brand-ink overflow-hidden border border-white/10 relative"
              data-testid={`fleet-${i}`}
            >
              {/* Spotlight clipped photo */}
              <div className="relative h-[220px] bg-brand-cream overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_60%,rgba(11,77,184,0.14),transparent_60%)]" />
                <img src={v.image} alt={v.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-white via-white/10 to-transparent" />
                <div className="absolute top-4 left-4 text-[10px] tracking-[0.24em] uppercase font-semibold bg-brand-blue text-white rounded-full px-3 py-1">{v.tag}</div>
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-2xl font-bold">{v.name}</h3>
                  <a
                    href={whatsappLink(`Hi, I'd like to book ${v.name}.`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-brand-blue text-white flex items-center justify-center hover:bg-brand-orange transition-colors"
                    aria-label={`Book ${v.name}`}
                    data-testid={`fleet-book-${i}`}
                  >
                    <ArrowUpRight className="w-4 h-4" />
                  </a>
                </div>
                <div className="mt-4 grid grid-cols-3 text-center text-xs text-brand-ink/70">
                  <div className="flex flex-col items-center gap-1"><Users className="w-4 h-4 text-brand-blue" /> {v.seats} seats</div>
                  <div className="flex flex-col items-center gap-1"><Snowflake className="w-4 h-4 text-brand-blue" /> A/C</div>
                  <div className="flex flex-col items-center gap-1"><Fuel className="w-4 h-4 text-brand-blue" /> Diesel</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-4 text-white/50 text-xs tracking-[0.2em] uppercase">← swipe to explore fleet →</div>
      </div>
    </section>
  );
}
